import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@pisaka/ui';
import { useModal } from '../../../../hooks/useModal';
import { MODAL_NAME_CREATE_FONT } from '../../../../constants/modalNamesConstants';
import { useCreateFont } from './useFonts';

const CreateFontModalContainer: FC = () => {
  const { t } = useTranslation();
  const { isOpenModal, onCloseModal } = useModal(MODAL_NAME_CREATE_FONT);

  const [fontName, setFontName] = useState('');
  const [alphabetCountryCode, setAlphabetCountryCode] = useState('ru');

  const onFontNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setFontName(e.target.value);
  const onAlphabetChange = (value: string) => setAlphabetCountryCode(value);

  const { isLoadingCreateFont, createFont } = useCreateFont();

  const alphabetOptions = [
    { text: t('fontCreator.alphabets.russian'), value: 'ru' },
    { text: t('fontCreator.alphabets.english'), value: 'en' },
    { text: t('fontCreator.alphabets.ukrainian'), value: 'ua' },
    { text: t('fontCreator.alphabets.belarusian'), value: 'by' },
    { text: t('fontCreator.alphabets.bashkir'), value: 'ru-ba' },
    { text: t('fontCreator.alphabets.tatar'), value: 'ru-ta' },
    { text: t('fontCreator.alphabets.kazakh'), value: 'kz' },
    { text: t('fontCreator.alphabets.armenian'), value: 'am' },
    { text: t('fontCreator.alphabets.georgian'), value: 'ge' },
    { text: t('fontCreator.alphabets.turkish'), value: 'tr' },
  ];

  const onClickCreateFont = async () => {
    if (fontName.length && alphabetCountryCode.length) {
      await createFont(fontName, alphabetCountryCode);
      onCloseModal();
    }
  };

  return (
    <Dialog open={isOpenModal} onOpenChange={(open) => !open && onCloseModal()}>
      <DialogContent className="w-[90vw] max-w-md">
        <DialogHeader>
          <DialogTitle>{t('fontCreator.createNewFont')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="font-name">{t('fontCreator.fontNameLabel')}</Label>
            <Input
              id="font-name"
              value={fontName}
              onChange={onFontNameChange}
              placeholder={t('fontCreator.fontNamePlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('fontCreator.alphabetLabel')}</Label>
            <Select value={alphabetCountryCode} onValueChange={onAlphabetChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {alphabetOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            className="w-full mt-2 bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground"
            onClick={onClickCreateFont}
            disabled={isLoadingCreateFont}
          >
            {isLoadingCreateFont ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('fontCreator.createButton')}
              </span>
            ) : (
              t('fontCreator.createButton')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFontModalContainer;
